import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useSession } from '@/hooks/useSession';
import { Loader } from '@/components/custom/loader';
import { Badge } from '@/components/ui/badge';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Dialog } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { DuelsAccept } from '../duels-accept';

const PLAYFUL_TAB_CLASSNAME =
  'flex-1 px-2 py-1 text-xs data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px';

function calculateTimeRemaining(expiration) {
  const now = new Date();
  const expirationTime = new Date(expiration);

  const differenceInMs = expirationTime - now;

  const hours = Math.floor((differenceInMs / (1000 * 60 * 60)) % 24);

  if (hours > 0) {
    return hours;
  }
}

export function DuelsPlayfulRequests({ type, data, setActiveTab, activeTab, updateDuelsAndRequests, loadingReceived, loadingSent }) {
  const { session } = useSession(false);
  const [acceptModal, setAcceptModal] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState();

  const userAvatar = session.user.image.url;
  const isLoading = type === 'received' ? loadingReceived : loadingSent;

  const getBadgeVariant = duel => {
    if (type === 'sent') {
      if (duel?.status === 'awaiting_acceptance') return 'amber-soft';
      if (duel?.status === 'refused') return 'rose-soft';
      return 'muted';
    }
    if (type === 'received') {
      if (activeTab === 'active') {
        const timeRemaining = calculateTimeRemaining(duel?.['expiration-date']);
        return timeRemaining < 3 ? 'danger-soft' : 'amber-soft';
      }
      if (activeTab === 'refused') return 'rose-soft';
      return 'muted';
    }
  };

  const getDuelStatus = duel => {
    if (type === 'received') {
      if (activeTab === 'refused') return 'Recusado';
      if (activeTab === 'expired') return 'Expirado';
      return `Expira em ${calculateTimeRemaining(duel?.['expiration-date'])}h`;
    }
    if (duel?.status === 'awaiting_acceptance' && type === 'sent') {
      return 'Aguardando';
    }

    switch (duel?.status) {
      case 'expired':
        return 'Expirado';
      case 'refused':
        return 'Recusado';
    }
  };

  const handleAcceptModal = duel => {
    if (type === 'received' && activeTab === 'active') {
      setSelectedDuel(duel);
      setAcceptModal(true);
    }
  };

  return (
    <section className="mb-6 rounded-2xl border border-neutral-lightest bg-white p-5 pb-3 shadow-card last:mb-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="mb-0 text-base font-extrabold text-neutral-900">Solicitações {type === 'received' ? 'recebidas' : 'enviadas'}</h3>

        {type === 'received' && (
          <Badge
            variant="rose-soft"
            size="sm"
            className="shrink-0 rounded-full"
          >
            {data.length} {data.length > 1 ? 'novas' : 'nova'}
          </Badge>
        )}
      </div>

      {type === 'received' && (
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="mt-3"
        >
          <TabsList className="w-full justify-between gap-1 bg-neutral-bright p-1">
            <TabsTrigger
              value="active"
              className={PLAYFUL_TAB_CLASSNAME}
            >
              Ativos
            </TabsTrigger>
            <TabsTrigger
              value="refused"
              className={PLAYFUL_TAB_CLASSNAME}
            >
              Recusados
            </TabsTrigger>
            <TabsTrigger
              value="expired"
              className={PLAYFUL_TAB_CLASSNAME}
            >
              Expirados
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isLoading && <Loader />}

      {!isLoading && data.length > 0 && (
        <div className="-mx-4 mt-4 flex max-h-[360px] flex-col gap-3 overflow-y-auto px-3">
          {data.map(item => {
            const opponentAvatar = item?.opponent?.image?.url;
            const isClickable = type === 'received' && activeTab === 'active';

            return (
              <div
                key={item.id}
                onClick={() => handleAcceptModal(item)}
                className={cn('flex items-center justify-between gap-2 rounded-xl border border-neutral-lightest p-3', isClickable && 'mb-2.5 cursor-pointer transition-shadow hover:shadow-card-hover')}
              >
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <AvatarWrapper
                      size="sm"
                      src={item?.['creator-id'] === session?.user?.id ? userAvatar : opponentAvatar}
                      text={session?.user?.name}
                    />
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[12px] leading-none shadow-sm">✨</span>
                  </div>

                  <FiX className="shrink-0 text-danger" />

                  <AvatarWrapper
                    size="sm"
                    src={item?.opponent?.id === session?.user?.id ? userAvatar : opponentAvatar}
                    text={item?.opponent?.name}
                  />
                </div>

                <Badge
                  variant={getBadgeVariant(item)}
                  size="sm"
                  className="shrink-0 rounded-full normal-case"
                >
                  {getDuelStatus(item)}
                </Badge>
              </div>
            );
          })}
        </div>
      )}

      {type === 'received' && data.length <= 0 && !loadingReceived && <p className="mb-0 mt-4 text-sm text-neutral-dark">Não há solicitações com este status</p>}
      {type === 'sent' && data.length <= 0 && !loadingSent && <p className="mb-0 mt-4 text-sm text-neutral-dark">Nenhuma solicitação enviada</p>}

      <Dialog
        open={acceptModal}
        onOpenChange={setAcceptModal}
      >
        <DuelsAccept
          open={acceptModal}
          setOpen={setAcceptModal}
          duel={selectedDuel}
          updateDuelsAndRequests={updateDuelsAndRequests}
        />
      </Dialog>
    </section>
  );
}
